import { LANDING_REGISTER_URL } from './config';
import communityBooksGenerated from './generated/communityBooks.generated.json';
import type { LandingTranslation } from './i18n';

export type HeroSlide = {
  id: number;
  number: string;
  label: string;
  imageDesktop: string;
  imageMobile: string;
  alt: string;
};

export type StorytellerReview = {
  id: string;
  author: string;
  role: string;
  quote: string;
};

export type EvolutionCardContent = {
  id: string;
  title: string;
  description: string;
};

export type EvolutionSceneSliderId = 'weather' | 'location' | 'time';

export type EvolutionSceneSliderSlide = {
  id: EvolutionSceneSliderId;
  tabLabel: string;
  badge: string;
  body: string;
  image: string;
};

export type LanguageChip = {
  label: string;
  accent: string;
};

export type CommunityBook = {
  id: string;
  title: string;
  genre: string;
  blurb: string;
  href: string;
  imageUrl: string;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  year?: number | null;
  location?: string | null;
  charactersCount?: number | null;
  chaptersCount?: number | null;
};

export type FaqItem = {
  id: string;
  question: string;
  answer: string;
};

export type LandingContent = {
  hero: {
    badge: string;
    title: string;
    description: string;
    bullets: string[];
    slides: HeroSlide[];
    mobileSlides: HeroSlide[];
  };
  storytellers: {
    title: string;
    reviews: StorytellerReview[];
  };
  evolutionGrid: {
    title: string;
    cards: EvolutionCardContent[];
    sceneSlider: {
      slides: EvolutionSceneSliderSlide[];
    };
    ribbonTitle: string;
    ribbonSubtitle: string;
  };
  languageWorld: {
    title: string;
    description: string;
    chips: LanguageChip[];
  };
  readyToBegin: {
    title: string;
    label: string;
    price: string;
    note: string;
    cta: string;
  };
  communityBooks: {
    title: string;
    books: CommunityBook[];
    cta: string;
  };
  faq: {
    title: string;
    items: FaqItem[];
  };
  bottomCta: {
    title: string;
    description: string;
    overlayText: string;
  };
};

const resolvedCommunityBooks = communityBooksGenerated as CommunityBook[];

const heroDesktopImages = [
  {
    desktop: '/hero/slide-1-desktopV3.png',
    mobile: '/hero/slide-1-mobile.png',
  },
  {
    desktop: '/hero/slide-2-desktopV2.png',
    mobile: '/hero/slide-2-mobile.png',
  },
  {
    desktop: '/hero/slide-3-desktopV3.png',
    mobile: '/hero/slide-3-mobile.png',
  },
] as const;

const heroMobileImages = [
  '/hero/slide-1-mobileV2.jpg',
  '/hero/slide-2-mobileV2.jpg',
  '/hero/slide-3-mobile.png',
  '/hero/slide-4-mobile.jpg',
] as const;

const sceneSliderImages: Record<EvolutionSceneSliderId, string> = {
  weather: '/landing/evolution-scene-weather.png',
  location: '/landing/evolution-scene-location.png',
  time: '/landing/evolution-scene-time.png',
};

const storytellerIds = ['jana', 'leo', 'amira'] as const;
const faqIds = [
  'what-is-lucid-book',
  'difference-from-ai-chat-apps',
  'free-to-try',
  'writing-experience',
  'own-worlds-and-characters',
  'play-books-from-others',
  'where-can-i-use-it',
  'what-ai-powers-lucid-book',
] as const;

const languageChipAccents = [
  '#F2C94C',
  '#7CC8FF',
  '#FF9BB0',
  '#F4D35E',
  '#6195FF',
  '#FFFFFF',
  '#FFD39B',
  '#B4F8C8',
  '#FF6B6B',
  '#8ED6FF',
  '#C8A8FF',
  '#FFB3D9',
] as const;

export function getLandingContent(t: LandingTranslation): LandingContent {
  const landing = t.landing;

  const fallbackCommunityBooks: CommunityBook[] = landing.communityBooks.fallbackBooks.map((book, index) => ({
    id: ['clockmaker-city', 'moon-garden', 'aerilon'][index] ?? `fallback-book-${index}`,
    title: book.title,
    genre: book.genre,
    blurb: book.blurb,
    href: LANDING_REGISTER_URL,
    imageUrl: '',
    authorName: book.authorName,
    authorAvatarUrl: '',
  }));

  const communityBooks = resolvedCommunityBooks.length > 0 ? resolvedCommunityBooks : fallbackCommunityBooks;

  return {
    hero: {
      badge: landing.hero.badge,
      title: landing.hero.title,
      description: landing.hero.description,
      bullets: landing.hero.bullets,
      slides: landing.hero.slides.map((label, index) => ({
        id: index,
        number: String(index + 1),
        label,
        imageDesktop: heroDesktopImages[index]?.desktop ?? heroDesktopImages[0].desktop,
        imageMobile: heroDesktopImages[index]?.mobile ?? heroDesktopImages[0].mobile,
        alt: label,
      })),
      mobileSlides: (landing.hero.slidesMobile ?? landing.hero.slides).map((label, index) => ({
        id: index,
        number: String(index + 1),
        label,
        imageDesktop:
          heroDesktopImages[Math.min(index, heroDesktopImages.length - 1)]?.desktop ??
          heroDesktopImages[0].desktop,
        imageMobile: heroMobileImages[index] ?? heroMobileImages[0],
        alt: label,
      })),
    },
    storytellers: {
      title: landing.storytellers.title,
      reviews: landing.storytellers.reviews.map((review, index) => ({
        id: storytellerIds[index] ?? `review-${index}`,
        author: review.author,
        role: review.role,
        quote: review.quote,
      })),
    },
    evolutionGrid: {
      title: landing.evolutionGrid.title,
      cards: [
        {
          id: 'create-book',
          title: landing.evolutionGrid.cards.createBook.title,
          description: landing.evolutionGrid.cards.createBook.description,
        },
        {
          id: 'scene-controls',
          title: landing.evolutionGrid.cards.sceneControls.title,
          description: landing.evolutionGrid.cards.sceneControls.description,
        },
        {
          id: 'chat-characters',
          title: landing.evolutionGrid.cards.chatCharacters.title,
          description: landing.evolutionGrid.cards.chatCharacters.description,
        },
        {
          id: 'relationships',
          title: landing.evolutionGrid.cards.relationships.title,
          description: landing.evolutionGrid.cards.relationships.description,
        },
        {
          id: 'share',
          title: landing.evolutionGrid.cards.share.title,
          description: landing.evolutionGrid.cards.share.description,
        },
      ],
      sceneSlider: {
        slides: landing.evolutionGrid.sceneSlider.slides.map((slide) => ({
          id: slide.id as EvolutionSceneSliderId,
          tabLabel: slide.tabLabel,
          badge: slide.badge,
          body: slide.body,
          image: sceneSliderImages[slide.id as EvolutionSceneSliderId],
        })),
      },
      ribbonTitle: landing.evolutionGrid.ribbonTitle,
      ribbonSubtitle: landing.evolutionGrid.ribbonSubtitle,
    },
    languageWorld: {
      title: landing.languageWorld.title,
      description: landing.languageWorld.description,
      chips: landing.languageWorld.chips.map((label, index) => ({
        label,
        accent: languageChipAccents[index % languageChipAccents.length] ?? '#FFFFFF',
      })),
    },
    readyToBegin: {
      title: landing.readyToBegin.title,
      label: landing.readyToBegin.label,
      price: landing.readyToBegin.price,
      note: landing.readyToBegin.note,
      cta: landing.readyToBegin.cta,
    },
    communityBooks: {
      title: landing.communityBooks.title,
      cta: landing.communityBooks.cta,
      books: communityBooks,
    },
    faq: {
      title: landing.faq.title,
      items: landing.faq.items.map((item, index) => ({
        id: faqIds[index] ?? `faq-${index}`,
        question: item.question,
        answer: item.answer,
      })),
    },
    bottomCta: {
      title: landing.bottomCta.title,
      description: landing.bottomCta.description,
      overlayText: landing.bottomCta.overlayText,
    },
  };
}
